
import _, { extend, each } from 'underscore';
import Promise from 'bluebird';
import { useContext, useCallback } from 'react';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { useWizardService, WIZARD_EVENTS } from '@bingads-webui-react/wizard';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import {
  loadBundleCountries,
  saveAdGroup,
  modelToViewModelForAdGroup,
  modelToViewModelForCampaign,
  readLocationCriterions,
  readDayTimeCriterions,
  readTargetingCriterions,
  readAudienceCriterions,
  getErrorsPlaceholders,
  loadAdGroup,
  loadCampaign,
  readPlacementCriterions,
} from '@bingads-webui-campaign-react/xandr-campaign';
import { getDealDetails, modelConstraints } from '@bingads-webui-campaign-react/xandr-video-campaign-shared-components';
import exclusionRepository from '@bingads-webui-campaign-legacy/advanced-settings/util/exclusion-repository';
import { ScopeLevel } from '@bingads-webui-campaign-react/constants';
import { ScenarioContext, ActivityContext } from '@bingads-webui-react/hoc-with-signal';
import { ScopeContext } from '@bingads-webui-react/scope-service';
import { useAudienceData } from '@bingads-webui-campaign-react/pmax-lite-data';
import {
  readDayTimeCriterions as campaignReadDayTimeCriterions,
  readLocationCriterions as campaignReadLocationCriterions,
  readDeviceCriterion as campaignReadDeviceCriterions,
} from '../../shared/model/campaign-criterion';
import { useDefaultAdGroup } from './use-default-adgroup/index';

import {
  isAudienceCampaign,
  isAudienceAdGroupEdit,
  isCTVAdGroupEdit,
  isDisplayAdsCampaign,
  isVideoAdsCampaign,
  isAudienceAdGroupCreation,
  isAdGroupCreation,
  isDealCampaign,
} from '../../step-constants';

export const useAdGroupRepository = () => {
  const i18n = useContext(I18nContext);
  const { send, state } = useWizardService();

  const serverContext = useContext(ServerContext);
  const { scope } = useContext(ScopeContext) || {};
  const scenarioContext = useContext(ScenarioContext);

  const activity = useContext(ActivityContext);
  const { cid, aid } = scope.values;

  const { ServicesEndpoints } = serverContext;
  const odataUrl = ServicesEndpoints && ServicesEndpoints.CampaignAPI;
  const isOnebox = odataUrl && odataUrl.toString().indexOf('redmond.corp') > 0;
  const [, getAudienceData] = useAudienceData({ postponeFetch: true, pathOverride: isOnebox ? '/Advertiser/V2' : 'Campaign/V2', audienceType: 'InMarket' });

  const inMarketAudienceRedesignEnabled = _.get(serverContext, ['DynamicPermissions', 'InMarketAudienceRedesign']);

  const {
    ads, flow, isAdGroupPublishing, isDefaultAdGroupStepModel,
  } = state.context.wizardData;

  const { defaultAdGroup: defaultAdGroupData, defaultTargeting } = useDefaultAdGroup(flow) || {};
  const isDealFlow = isDealCampaign(flow);

  const loadAudienceData = useCallback(
    () => getAudienceData()
      .then(audienceList => _.filter(audienceList, audience => audience && audience.Type === 'InMarket'))
    , [getAudienceData]
  );

  const allSettledPromiseWrapper = (promises) => {
    const wrappedPromises = promises.map(p =>
      p.then(
        val => ({ status: 'fulfilled', value: val }),
        err => ({ status: 'rejected', reason: err })
      ));
    return Promise.all(wrappedPromises);
  };

  const load = () => {
    const savedCampaign = state.context.wizardData.campaign;
    const savedAdGroup = state.context.wizardData.adGroup;

    const adGroupId = savedAdGroup ? savedAdGroup.Id : null;
    const campaignId = savedCampaign ? savedCampaign.Id : null;

    let loadPromises = {
      bundleCountriesAndLocationCriterions: loadBundleCountries({
        cid,
        aid,
        serverContext,
      }),
      campaign: loadCampaign(cid, aid, campaignId),
      campaignLocationCriterions: campaignReadLocationCriterions({
        cid, aid, campaignId,
      }),
      campaignDeviceCriterions: campaignReadDeviceCriterions({
        cid, aid, campaignId,
      }),
      campaignDayTimeCriterions: campaignReadDayTimeCriterions({
        cid, aid, campaignId,
      }),
    };

    if (isDealFlow) {
      loadPromises = extend(loadPromises, {
        dealDetails: getDealDetails(cid, aid, campaignId, serverContext),
      });
    }

    if (adGroupId) {
      loadPromises = extend(loadPromises, {
        adGroup: loadAdGroup(cid, aid, campaignId, adGroupId),
        placementCriterions: readPlacementCriterions({
          cid, aid, campaignId, adGroupId,
        }),
        locationCriterions: readLocationCriterions({
          cid, aid, campaignId, adGroupId,
        }),
        dayTimeCriterions: readDayTimeCriterions({
          cid, aid, campaignId, adGroupId,
        }),
        targetCriterions: readTargetingCriterions({
          cid, aid, campaignId, adGroupId,
        }),
        audienceCriterions: readAudienceCriterions({
          cid, aid, campaignId, adGroupId,
        }),
        exclusions: exclusionRepository.load(campaignId, adGroupId, ScopeLevel.AdGroup),
      });
    }

    if (inMarketAudienceRedesignEnabled) {
      loadPromises = extend(loadPromises, {
        audienceList: loadAudienceData(),
      });
    }

    return allSettledPromiseWrapper(Object.values(loadPromises))
      .then((results) => {
        const resultMap = Object.keys(loadPromises).reduce((acc, key, index) => {
          if (results[index].status === 'fulfilled') {
            acc[key] = results[index].value;
          }
          return acc;
        }, {});

        const errors = results.reduce((acc, result) => {
          if (result.status === 'rejected') {
            acc.push(...result.reason.errors);
          }
          return acc;
        }, []);

        const {
          adGroup = {},
          campaign = {},
          locationCriterions = null,
          campaignLocationCriterions,
          campaignDeviceCriterions,
          bundleCountriesAndLocationCriterions,
          dayTimeCriterions = null,
          campaignDayTimeCriterions,
          targetCriterions = null,
          audienceCriterions = null,
          exclusions: [campaignExclusions, adgroupExclusions] = [null, null],
          audienceList,
          placementCriterions = null,
          dealDetails = null,
        } = resultMap || {};

        const adGroupData = modelToViewModelForAdGroup({
          adGroupModel: adGroup,
          campaignModel: campaign,
          locationCriterionsModel: locationCriterions ? locationCriterions.value : [],
          campaignLocationCriterionsModel: campaignLocationCriterions ? campaignLocationCriterions.value : [],
          campaignDeviceCriterions: campaignDeviceCriterions ? campaignDeviceCriterions.value : [],
          bundleCountries: bundleCountriesAndLocationCriterions ? bundleCountriesAndLocationCriterions.bundleCountries : [],
          dayTimeCriterionsModel: dayTimeCriterions ? dayTimeCriterions.value : [],
          targetCriterionsModel: targetCriterions ? targetCriterions.value : [],
          audienceCriterionsModel: audienceCriterions ? audienceCriterions.value : [],
          placementCriterionsModel: placementCriterions ? placementCriterions.value : [],
          i18n,
          isAudienceAds: isAudienceCampaign(flow) || isAudienceAdGroupEdit(flow) || isAudienceAdGroupCreation(flow),
          isDisplayAds: isDisplayAdsCampaign(flow),
          isVideoAds: isVideoAdsCampaign(flow),
          isAudienceAdGroupEdit: isAudienceAdGroupEdit(flow),
          isEnableMSANDisplayCampaignsEnabled: serverContext.DynamicPermissions && serverContext.DynamicPermissions.EnableMSANDisplayCampaigns,
          isEnableMSANVideoCampaignsEnabled: serverContext.DynamicPermissions && serverContext.DynamicPermissions.EnableMSANVideoCampaigns,
          defaultAdGroupData,
          defaultTargeting,
          separateIMA: inMarketAudienceRedesignEnabled,
          ads,
        });

        if (isDefaultAdGroupStepModel && (isDisplayAdsCampaign(flow) || isVideoAdsCampaign(flow))) {
          adGroupData.data.StartDate = new Date();
        }

        let adGroupModelWithConstraintsPromise = Promise.resolve(adGroupData.data);

        if (isDealFlow && dealDetails) {
          adGroupModelWithConstraintsPromise = modelConstraints(dealDetails, adGroupData.data, serverContext);
          send(WIZARD_EVENTS.UPDATE_WIZARD_DATA, { deal: dealDetails });
        }

        return adGroupModelWithConstraintsPromise
          .then((constraintModel) => {
            adGroupData.data = constraintModel;
          })
          .then(() => {
            const campaignData = modelToViewModelForCampaign({
              campaignModel: campaign,
              // eslint-disable-next-line no-param-reassign
              dayTimeCriterionsModel: campaignDayTimeCriterions ? each(campaignDayTimeCriterions.value, (cr) => { cr.Id = ''; }) : [],
            });

            return {
              adGroup: adGroupData.data,
              campaign: campaignData.data,
              ...bundleCountriesAndLocationCriterions,
              exclusions: {
                campaignExclusions,
                adgroupExclusions,
              },
              audienceList,
              ...getErrorsPlaceholders(),
              errors,
            };
          });
      });
  };

  const save = ({ data }) => {
    const {
      adGroup, bundleCountries, bundleLocationCriterions, exclusions,
    } = data;

    if (isAdGroupPublishing || isAdGroupCreation(flow)) {
      adGroup.Status = 'Active';
    } else if (!isAudienceAdGroupEdit(flow) && !isCTVAdGroupEdit(flow)) {
      adGroup.Status = 'Paused';
    } else if (isAudienceAdGroupEdit(flow) && adGroup.Status === 'Draft') {
      adGroup.Status = undefined;
    }

    const saveAdGroupPromise = saveAdGroup({
      adGroup: { data: adGroup },
      bundleCountries,
      bundleLocationCriterions,
      campaign: state.context.wizardData.campaign,
      isDisplayPlus: true,
      flow,
    }, {
      scenario: scenarioContext.name,
      activityLogger: scenarioContext.createActivity('saveAdGroup'),
      i18n,
      serverContext,
    });

    const savePromises = [saveAdGroupPromise];

    if (isAudienceAdGroupEdit(flow)) {
      const saveExclusionsData = {
        CampaignId: state.context.wizardData.campaign.Id,
        AdGroupId: adGroup.Id,
        AdvancedSettings: {
          ExcludedSites: exclusions.adgroupExclusions.ExcludedSites,
        },
      };
      const saveExclusionsPromise = exclusionRepository.saveExtensions(saveExclusionsData, ScopeLevel.AdGroup);
      savePromises.push(saveExclusionsPromise);
    }

    return new Promise((resolve, reject) => {
      allSettledPromiseWrapper(savePromises).then((results) => {
        const allSuccess = _.every(results, result => result.status === 'fulfilled');

        if (allSuccess) {
          if (activity) {
            activity.signal(`onAdGroupSavedSuccessfully - Status: ${adGroup.Status}, Id: ${adGroup.Id}`);
          }

          const adGroupMtModel = results[0].value.adGroup.data;
          send(WIZARD_EVENTS.UPDATE_WIZARD_DATA, { adGroup: adGroupMtModel, isDefaultAdGroupStepModel: false });

          return resolve(results[0].value);
        }

        const errors = _.map(results, (result) => {
          if (activity) {
            activity.signal(`onAdGroupSavedError - error ${JSON.stringify(result)}`);
          }
          if (result.status === 'fulfilled') {
            return null;
          }

          const { serverErrors = {} } = result.reason;

          if (result.reason.NegativeSitesResult) {
            const { NegativeSitesResult } = result.reason;
            serverErrors.property = 'webExclusions';
            serverErrors.NegativeSitesResult = NegativeSitesResult.EntityErrors;
          }

          return serverErrors;
        });

        // eslint-disable-next-line prefer-promise-reject-errors
        return reject({ error: _.flatten(errors.filter(val => val !== null)) });
      });
    });
  };

  return {
    load,
    save,
  };
};
